import { ReactElement, useEffect, useState } from 'react';
import collect from 'collect.js';
import CompanyManager from '../../services/api/CompanyManager';
import { CompanyDetailsInterface, ProductInterface, RewardInterface } from '../../services/exports/Interfaces';
import { useSelector } from 'react-redux';
import Highlighter from 'react-highlight-words';
import Theme from '../../components-library/Theme';
import { useTranslation } from 'react-i18next';
import useCashbackProgram from '../cashback/useCashbackProgram';
import useGoogleReview from './useGoogleReview';
import useRewardAccount from '../loyalty/useRewardAccount';
import { selectCompanyId } from '../../store/slices/InitialDataSlice';
import useGetCompanyMenusApi from '../api/companies/useGetCompanyMenusApi';

interface MessageConfigInterface {
  highlightWords?: boolean;
  type?: 'short' | 'reward' | 'rewardWithPrefix';
}

interface GoogleReviewSuggestedProductsHookInterface {
  findSuggestedProducts: () => Promise<void>;
  suggestedProducts: ProductInterface[];
  loading: boolean;
  getTitle: (className?: string) => any;
  message: (config?: MessageConfigInterface) => any;
}

interface Props {
  autoLoad?: boolean;
  company?: CompanyDetailsInterface;
}

export default function useGoogleReviewSuggestedProducts(props?: Props): GoogleReviewSuggestedProductsHookInterface {
  const { t } = useTranslation(null, { keyPrefix: 'Hooks:GoogleReview:UseGoogleReviewSuggestedProducts' });

  const companyId = props?.company?.id ?? useSelector(selectCompanyId);
  const { data: menusData } = useGetCompanyMenusApi();

  const { hasCashbackProgram, formatPointsAsMoney } = useCashbackProgram();
  const { balance, rewards, maxReward } = useRewardAccount();
  const { googlePromotion } = useGoogleReview();

  const [loading, setLoading] = useState<boolean>(false);
  const [suggestedProducts, setSuggestedProducts] = useState<ProductInterface[]>([]);

  useEffect(() => {
    if (props?.autoLoad && !!companyId) {
      findSuggestedProducts();
    }
  }, [props?.autoLoad, companyId]);

  async function findSuggestedProducts(): Promise<void> {
    if (!maxReward) {
      return findDiscountedProducts();
    }

    setLoading(true);
    const { response, success } = await CompanyManager.getFreeMenuItems(companyId);

    if (!success) {
      return findDiscountedProducts();
    }

    if (response?.data?.data?.length === 0) {
      return findDiscountedProducts();
    }

    setSuggestedProducts(
      collect(response?.data?.data)
        .map((product: ProductInterface) => {
          return {
            ...product,
            free_eligible: true,
          };
        })
        .toArray(),
    );
    toggleInitialLoading();
  }

  function findDiscountedProducts(): void {
    const discountedProducts: ProductInterface[] = collect(menusData?.products)
      .sortByDesc((product: ProductInterface) => product?.base_price)
      .filter((item: ProductInterface) => !!item?.thumbnail_url && item?.free_eligible)
      .toArray();

    if (discountedProducts?.length !== 0) {
      setSuggestedProducts(discountedProducts);
      toggleInitialLoading();

      return;
    }

    setSuggestedProducts(
      collect(menusData?.products)
        .sortByDesc((product: ProductInterface) => product?.base_price)
        .filter((item: ProductInterface) => !!item?.thumbnail_url)
        .toArray(),
    );
    toggleInitialLoading();
  }

  const toggleInitialLoading = () => setTimeout(() => setLoading(false), 500);

  const getTitle = (className?: string) => {
    if (hasCashbackProgram) {
      return (
        <div className={className}>
          <p className="text-center">{t('cashback.title')}</p>
          <h5 className="text-center text-brand-primary">
            {t('cashback.description', {
              value: googlePromotion?.reward,
              moneyValue: formatPointsAsMoney(googlePromotion?.reward),
            })}
          </h5>
        </div>
      );
    }

    const product = collect(suggestedProducts).first();

    return (
      <div className={className}>
        <p>{t('product_suggestions.title')}</p>
        <h5 className="text-brand-primary">{t('product_suggestions.description', { product: product?.name })}</h5>
      </div>
    );
  };

  function getMessage(config?: MessageConfigInterface): any {
    const Highlight = ({ children }: { children: ReactElement }) => (
      <div className="inline text-brand-primary">{children}</div>
    );

    const product = collect(suggestedProducts)?.first();
    const reward = collect(rewards).last(
      (reward: RewardInterface) => balance + googlePromotion?.reward >= reward?.cost && reward?.discount !== 100,
    );

    if (product?.free_eligible) {
      if (suggestedProducts?.length > 1) {
        const message = () => {
          switch (config?.type) {
            case 'short':
              return t('product_suggestions.types.multiple_free_rewards.message_short', {
                product: product?.name,
              });
            case 'reward':
              return t('product_suggestions.types.multiple_free_rewards.message_reward', {
                product: product?.name,
              });
            case 'rewardWithPrefix':
              return t('product_suggestions.types.multiple_free_rewards.message_reward_prefix', {
                product: product?.name,
              });
            default:
              return t('product_suggestions.types.multiple_free_rewards.message_default', {
                product: product?.name,
              });
          }
        };

        return config?.highlightWords ? (
          // @ts-expect-error Weird ts error
          <Highlighter
            activeClassName=""
            highlightStyle={{ color: Theme.color.brand.primary }}
            searchWords={[product?.name, '& more', '& mehr']}
            autoEscape={true}
            textToHighlight={message()}
            highlightTag={Highlight}
          />
        ) : (
          message()
        );
      }

      const message = () => {
        switch (config?.type) {
          case 'short':
            return t('product_suggestions.types.free_reward.message_short', {
              product: product?.name,
            });
          case 'reward':
            return t('product_suggestions.types.free_reward.message_reward', {
              product: product?.name,
            });
          case 'rewardWithPrefix':
            return t('product_suggestions.types.free_reward.message_reward_prefix', {
              product: product?.name,
            });
          default:
            return t('product_suggestions.types.free_reward.message_default', {
              product: product?.name,
            });
        }
      };

      return config?.highlightWords ? (
        // @ts-expect-error Weird ts error
        <Highlighter
          activeClassName=""
          highlightStyle={{ color: Theme.color.brand.primary }}
          searchWords={[product?.name, '& more', '& mehr']}
          autoEscape={true}
          textToHighlight={message()}
          highlightTag={Highlight}
        />
      ) : (
        message()
      );
    }

    const message = () => {
      switch (config?.type) {
        case 'short':
          return t('product_suggestions.types.discount.message_short', {
            discount: reward?.discount,
          });
        case 'reward':
          return t('product_suggestions.types.discount.message_reward', {
            discount: reward?.discount,
          });
        case 'rewardWithPrefix':
          return t('product_suggestions.types.discount.message_reward_prefix', {
            discount: reward?.discount,
          });
        default:
          return t('product_suggestions.types.discount.message_default', {
            discount: reward?.discount,
          });
      }
    };

    return config?.highlightWords ? (
      // @ts-expect-error Weird ts error
      <Highlighter
        activeClassName=""
        highlightStyle={{ color: Theme.color.brand.primary }}
        searchWords={[reward?.discount?.toString(), '%']}
        autoEscape={true}
        textToHighlight={message()}
        highlightTag={Highlight}
      />
    ) : (
      message()
    );
  }

  return {
    findSuggestedProducts,
    suggestedProducts,
    loading,
    getTitle,
    message: getMessage,
  };
}
